// CommentComposer.tsx — top-level comment composer. Signed-out → SignInCta.
import { useState } from 'react';
import { useReplyDraft } from '@/hooks/useReplyDraft';
import type { ChangeEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { ConsolePanel, Avatar, Button } from '@/ui';
import { useSession } from '@/hooks';
import { useWriteGate } from '@/hooks/writeGate';
import { SignInCta } from '@/components/SignInCta';
import { TwoFactorCta } from '@/components/TwoFactorCta';

export interface CommentComposerProps {
  draftScope?: string;
  /** Submit a trimmed, non-empty comment body. */
  onSubmit: (body: string) => void | Promise<void>;
}

const Wrap = styled(ConsolePanel)`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
`;

const Col = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Input = styled.textarea`
  width: 100%;
  min-height: 56px;
  resize: none;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  outline: none;
  &::placeholder {
    color: ${({ theme }) => theme.color.textMuted};
  }
  &:focus-visible {
    border-bottom-color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export function CommentComposer({ onSubmit, draftScope }: CommentComposerProps) {
  const { isAuthenticated, currentUser } = useSession();
  const gate = useWriteGate();
  const { value, setValue, saved, restore, discard } = useReplyDraft(draftScope);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const trimmed = value.trim();

  if (!isAuthenticated) {
    return <SignInCta message="Sign in to join the conversation." />;
  }
  // 1.27.70: on the Videos pages, commenting needs two-factor sign-in (no-op elsewhere).
  if (gate.locked) {
    return <TwoFactorCta message="Turn on two-factor sign-in to comment on videos." />;
  }

  async function submit() {
    if (!trimmed || busy) return;
    setBusy(true); setError('');
    try { await onSubmit(trimmed); setValue(''); }
    catch { setError('Could not post. Your draft is still here. Try again.'); }
    finally { setBusy(false); }
  }

  function keydown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <Wrap>
      <Avatar name={currentUser?.name ?? 'User'} size={40} />
      <Col>
        {saved && <div role="status">You have an unfinished comment. <Button variant="ghost" onClick={restore}>Restore draft</Button><Button variant="ghost" onClick={discard}>Discard</Button></div>}
        {error && <p role="alert">{error}</p>}
        <Input
          value={value}
          disabled={busy}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
          onKeyDown={keydown}
          placeholder="Add a comment…"
          aria-label="Add a comment"
        />
        <Actions>
          <Button variant="primary" disabled={!trimmed || busy} onClick={() => void submit()}>
            Post
          </Button>
        </Actions>
      </Col>
    </Wrap>
  );
}
